import React, { lazy, Suspense, useEffect } from "react";
import { Box, CircularProgress, Typography, Divider, Container } from "@mui/material";
import useSEO from "../../hooks/useSEO";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaidIcon from '@mui/icons-material/Paid';
import SecurityIcon from '@mui/icons-material/Security';
import HeaderLayout from "../../components/HeaderLayout/HeaderLayout";
import backdrop from "../../components/HeadingPage/assets/HeaderbgImage.svg";
import demoImage from "../../components/HeadingPage/assets/DemoImage.svg";
import financeBgImage from "../HomePage/assets/UseCasesBgImage.svg";
import invoiceProcessingImage from "../HomePage/assets/EmployeesImage.svg";
import fraudDetectionImage from "../HomePage/assets/EmployeesImage.svg";
import complianceImage from "../HomePage/assets/EmployeesImage.svg";
const FeaturesPageLayout = lazy(() => import("../../components/FeaturesPageLayout/FeaturesPageLayout"));
const UseCasesSection = lazy(() => import("../../components/UseCases/UseCase"));
const Testimonials = lazy(() => import("../../components/CompliantFold/Testimonials"));
const CustomUsecaseForm = lazy(() => import("../../components/CustomUsecaseForm/CustomUsecaseForm"));

// Enhanced loading fallback with better styling
const LoadingFallback = () => (
    <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        py: { xs: 6, sm: 8, md: 10 },
        minHeight: "200px"
    }}>
        <CircularProgress size={36} />
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, opacity: 0.8 }}
        >
            Loading...
        </Typography>
    </Box>
);

// Enhanced lazy loading component with better placeholder
const LazyComponent = ({ children, threshold = 0.1 }) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold, rootMargin: "200px" }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [threshold]);

    return (
        <div ref={ref}>
            {isVisible ? children : (
                <Box sx={{
                    height: { xs: "150px", sm: "180px", md: "200px" },
                    width: "100%",
                    transition: "height 0.3s ease"
                }} />
            )}
        </div>
    );
};

export default function HumanResources() {
    useSEO({
        title: "HR Solutions - Beanbag AI",
        description: "Streamline your HR processes with Beanbag AI. Automate hiring, onboarding, and employee management with intelligent workflows.",
        canonical: "https://www.beanbag.ai/hr",
        keywords: "HR automation, human resources AI, employee onboarding, HR workflow, employee management",

        ogTitle: "HR Automation Solutions | Beanbag AI",
        ogDescription: "Empower your HR team with intelligent automation for hiring, onboarding, and employee management",
        ogImage: "https://www.beanbag.ai/images/hr-solution-card.jpg",
        ogUrl: "https://www.beanbag.ai/hr",
        ogType: "website",

        twitterCard: "summary_large_image",
        twitterSite: "@BeanbagAI",

        jsonLd: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "HR Automation Solutions",
            "url": "https://www.beanbag.ai/hr",
            "description": "Beanbag AI empowers HR teams to streamline employee management, hiring processes, and compliance through intelligent automation.",
            "isPartOf": {
                "@type": "WebSite",
                "name": "Beanbag AI",
                "url": "https://www.beanbag.ai"
            }
        }
    });

    useEffect(() => {
        // Preload critical components
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(() => {
                import("../../components/KeyFeatures/KeyFeatures");
                import("../../components/UseCases/UseCase");
            });
        }

        // Load non-critical components when tab is visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                import("../../components/CompliantFold/Compliant");
                import("../../components/IntegrationSupport/IntegrationSupport");
                import("../../components/CustomUsecaseForm/CustomUsecaseForm");
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const hrFeatures = [
        {
            id: 1,
            title: "Boost Efficiency with Smart Automation",
            icon: <AccountBalanceIcon fontSize="large" />,
            description: "Our AI agents handle repetitive tasks like data entry, employee updates, and payroll processing, cutting down manual effort and speeding up workflows. Save time and let your HR team tackle strategic priorities",
            // benefits: [
            //     { primary: "70% Time Savings", secondary: "Automate repetitive tasks and free up your HR team for strategic work." },
            //     { primary: "Error Reduction", secondary: "Eliminate manual data entry mistakes with AI-powered processes." },
            //     { primary: "24/7 Availability", secondary: "Automated workflows continue running even outside business hours." }
            // ],
            // howItWorks: [
            //     { primary: "Process Mapping", secondary: "Identify repetitive HR tasks suitable for automation." },
            //     { primary: "AI Configuration", secondary: "Set up intelligent agents to handle specific workflows." },
            //     { primary: "Continuous Learning", secondary: "The system improves over time as it processes more tasks." }
            // ]
        },
        {
            id: 2,
            title: "Tailored Solutions for Every HR Need",
            icon: <ReceiptIcon fontSize="large" />,
            description: "From hiring to offboarding, our vast library of pre-built templates covers all HR use cases. Customize them with our drag-and-drop builder or dive into low-code options for complex workflows—flexibility is built in",
            // benefits: [
            //     { primary: "Quick Implementation", secondary: "Deploy ready-made templates in minutes, not months." },
            //     { primary: "Customization Options", secondary: "Adapt workflows to your specific HR processes and policies." },
            //     { primary: "No Technical Expertise Required", secondary: "User-friendly interface for HR professionals of all technical levels." }
            // ],
            // howItWorks: [
            //     { primary: "Template Selection", secondary: "Choose from our library of HR-specific workflow templates." },
            //     { primary: "Customization", secondary: "Modify templates with our intuitive drag-and-drop interface." },
            //     { primary: "Deployment", secondary: "Launch your customized workflow with a single click." }
            // ]
        },
        {
            id: 3,
            title: "Empower Your Team with Mr. Bean AI",
            icon: <PaidIcon fontSize="large" />,
            description: "Tell Mr. Bean AI what you need—say, a new onboarding process—and watch it craft a workflow in minutes. This agentic AI assistant learns your preferences, delivering personalized productivity boosts",
            // benefits: [
            //     { primary: "Natural Language Interface", secondary: "Simply describe what you need in plain English." },
            //     { primary: "Personalized Learning", secondary: "Mr. Bean AI adapts to your preferences and company policies." },
            //     { primary: "Rapid Development", secondary: "Create complex workflows in minutes instead of days." }
            // ],
            // howItWorks: [
            //     { primary: "Natural Request", secondary: "Tell Mr. Bean AI what HR process you need to create." },
            //     { primary: "AI Generation", secondary: "The assistant creates a draft workflow based on your description." },
            //     { primary: "Review and Refine", secondary: "Make adjustments and deploy when ready." }
            // ]
        },
        {
            id: 4,
            title: "Ensure Accuracy and Compliance",
            icon: <SecurityIcon fontSize="large" />,
            description: "Eliminate errors with automated data handling and stay compliant with regulations. Our platform integrates securely with your HR systems, keeping employee data safe and consistent.",
            // benefits: [
            //     { primary: "Regulatory Compliance", secondary: "Built-in rules ensure adherence to HR regulations and policies." },
            //     { primary: "Audit Trails", secondary: "Comprehensive logs for all HR activities and changes." },
            //     { primary: "Data Security", secondary: "Enterprise-grade security protects sensitive employee information." }
            // ],
            // howItWorks: [
            //     { primary: "Policy Implementation", secondary: "Encode HR policies and compliance requirements into workflows." },
            //     { primary: "Automated Checks", secondary: "The system flags potential compliance issues before they occur." },
            //     { primary: "Secure Integration", secondary: "Connect safely with your existing HR systems while maintaining data integrity." }
            // ]
        },
        {
            id: 5,
            title: "Scale with Ease",
            icon: <SecurityIcon fontSize="large" />,
            description: "Whether you're managing a handful of employees or thousands, WorkflowBean grows with you. Seamless integrations with tools like HRIS and payroll systems keep your operations smooth and connected.",
            // benefits: [
            //     { primary: "Flexible Growth", secondary: "Handle increasing HR workloads without adding headcount." },
            //     { primary: "Seamless Integrations", secondary: "Connect with all major HRIS, payroll, and HR tech platforms." },
            //     { primary: "Consistent Experience", secondary: "Maintain quality HR service regardless of company size." }
            // ],
            // howItWorks: [
            //     { primary: "API Connections", secondary: "Establish secure connections with your existing HR technology stack." },
            //     { primary: "Elastic Resources", secondary: "The platform automatically scales to handle your growing needs." },
            //     { primary: "Centralized Management", secondary: "Monitor and manage all HR workflows from a single dashboard." }
            // ]
        }
    ];

    const hrCasesData = [
        {
            id: 1,
            image: invoiceProcessingImage,
            title: "Faster Onboarding",
            description: "Automate document collection and employee setup, reducing onboarding time by up to 50%",
        },
        {
            id: 2,
            image: fraudDetectionImage,
            title: "Improved Productivity",
            description:
                "Free HR staff from mundane tasks, letting them focus on talent development and engagement",
        },
        {
            id: 3,
            image: complianceImage,
            title: "Cost Savings",
            description:
                "Cut operational costs by minimizing manual work and streamlining processes across departments.",
        },
    ];


    return (
        <Box sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden"
        }}>
            {/* Hero Section */}
            <Box sx={{
                mb: { xs: 4, sm: 6, md: 8, lg: 10 },
                px: { xs: 0, sm: 2, md: 3 }
            }}>
                <HeaderLayout
                    backgroundImage={backdrop}
                    title="Automate HR Processes with Agentic AI Apps"
                    subtitle="Whether you're a small business or a large enterprise, our no-code and low-code solutions bring simplicity, scalability, and smart automation to your HR workflows"
                    imageUrl={demoImage}
                    imageAlt="Platform Demo"
                    ctaButtonText="Get Started"
                    ctaButtonLink="https://flow.beanbag.ai/signup"
                    secondaryButtonText="Request a Demo"
                    showSecondaryButton={true}
                />
            </Box>

            {/* Features Section */}
            <Box sx={{
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 2, sm: 3, md: 0 }
            }}>
                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <FeaturesPageLayout
                            title={
                                <>
                                    Why Automate HR with <br />
                                    Beanbag AI?
                                </>
                            }
                            description="HR teams are constantly balancing administrative tasks with strategic initiatives. Beanbag AI helps you automate the routine so you can focus on what matters most - your people."
                            sectionTitle="How It Works"
                            features={hrFeatures}
                            backgroundColor="#121212"
                            containerProps={{
                                py: { xs: 0, sm: 8, md: 10, lg: 12 },
                                px: { xs: 2, sm: 3, md: 4 }
                            }}

                        />
                    </Suspense>
                </LazyComponent>
            </Box>

            {/* Use Cases Section */}
            <Box sx={{
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 4, sm: 6, md: 8 }
            }}>
                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <UseCasesSection
                            title="Why Choose Beanbag AI"
                            backgroundImage={financeBgImage}
                            cardsData={hrCasesData}
                        />
                    </Suspense>
                </LazyComponent>
            </Box>

            {/* Testimonials Section */}
            <Container
                maxWidth={false}
                sx={{
                    width: "100%",
                    maxWidth: {
                        xs: "100%",
                        sm: "95%",
                        md: "90%",
                        lg: "1200px"
                    },
                    px: { xs: 2, sm: 3, md: 4 }, // Add responsive padding
                    py: { xs: 4, sm: 6, md: 8 },
                    mx: "auto", // Center the container
                }}>
                <Divider
                    sx={{
                        backgroundColor: "rgba(68, 68, 68, 0.8)",
                        width: "100%",
                        height: "1px",
                        my: { xs: 4, sm: 5, md: 6, lg: 7 },
                    }}
                />
                <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                        fontSize: { xs: "24px", sm: "34px", md: "42px", lg: "48px" },
                        color: "white",
                        fontWeight: 600,
                        textAlign: "center",
                        textShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                        mb: { xs: 3, sm: 4, md: 5, lg: 6 },
                        px: { xs: 1, sm: 2, md: 0 }
                    }}
                >
                    Trusted by HR Teams Worldwide
                </Typography>

                <Box sx={{
                    px: { xs: 0, sm: 1, md: 2 },
                    py: { xs: 2, sm: 3, md: 4 }
                }}>
                    <LazyComponent>
                        <Suspense fallback={<LoadingFallback />}>
                            <Testimonials />
                        </Suspense>
                    </LazyComponent>
                </Box>

                <Divider
                    sx={{
                        backgroundColor: "rgba(68, 68, 68, 0.8)",
                        width: "100%",
                        height: "1px",
                        mt: { xs: 4, sm: 5, md: 6 },
                        mb: { xs: 6, sm: 8, md: 10 }
                    }}
                />
            </Container>

            {/* Contact Form Section */}
            <Box sx={{
            }}>
                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <CustomUsecaseForm />
                    </Suspense>
                </LazyComponent>
            </Box>
        </Box>
    );
}